/**
 * 🟥 CUADRO NEGRO Panel Conversión (feb-2026).
 *
 * Wrapper que escucha el evento global `damyxia:open-subscription-panel`
 * y monta el `SubscriptionUpgradeOverlay`. Vive una sola vez a nivel
 * App (no dentro del Dashboard) para que el overlay esté disponible en
 * CUALQUIER ruta — Home, Builder, Configuración, Planes y Beneficios,
 * etc.
 *
 * Lectura de Premium: se hace best-effort en el momento de abrir
 * (chequeo localStorage / /api/auth/me). Si el usuario ya es Premium,
 * no abrimos el overlay y mostramos un toast amable — requisito §6.2
 * del Cuadro Negro.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import SubscriptionUpgradeOverlay from './SubscriptionUpgradeOverlay';

const SUBSCRIPTION_PANEL_FLAG = 'damyxia.openSubscriptionPanelOnHome';

const SubscriptionUpgradeOverlayMount = () => {
  const [isOpen, setOpen] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  // 🟪 P25.30 · Fix: NUNCA confiar en localStorage.isPremium como
  // cortocircuito — puede estar contaminado por una sesión anterior en
  // el mismo navegador (owner premium → Free). Siempre pegar a
  // /api/auth/me antes de decidir si abrir o no el overlay.
  const checkPremium = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const prem = Boolean(res?.data?.is_premium);
      try { localStorage.setItem('isPremium', prem ? 'true' : 'false'); } catch { /* ignore */ }
      return prem;
    } catch {
      // Si no podemos verificar, asumir NO premium → mejor mostrar el
      // overlay que cerrarle el camino al usuario.
      return false;
    }
  }, []);

  const handleOpen = useCallback(async () => {
    const prem = await checkPremium();
    setIsPremium(prem);
    if (prem) {
      // §6.2: usuario Premium → no abrir panel; toast informativo.
      toast.info('Ya tenés Plan Premium activo. ¡Gracias por apoyar DamyxIA!', { duration: 4000 });
      return;
    }
    setOpen(true);
  }, [checkPremium]);

  useEffect(() => {
    const onEvent = () => { handleOpen(); };
    window.addEventListener('damyxia:open-subscription-panel', onEvent);

    // Flag sessionStorage (navegaciones desde otras rutas).
    try {
      if (sessionStorage.getItem(SUBSCRIPTION_PANEL_FLAG) === '1') {
        sessionStorage.removeItem(SUBSCRIPTION_PANEL_FLAG);
        handleOpen();
      }
    } catch { /* ignore */ }

    return () => window.removeEventListener('damyxia:open-subscription-panel', onEvent);
  }, [handleOpen]);

  return (
    <SubscriptionUpgradeOverlay
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      isPremium={isPremium}
    />
  );
};

export default SubscriptionUpgradeOverlayMount;
